/**
 * Transport-failure vocabulary shared by every SDK call path.
 *
 * A `fetch` that does not hand back a parseable JSON body has TWO very
 * different meanings, and collapsing them is what made a completed run look
 * like a failed one (papan-ai/papan#471):
 *
 *   - **`invalid_response`** — the server ANSWERED and the answer was junk, but
 *     the status says the request never reached the runner (a 4xx refusal, a
 *     misrouted 200 serving an SPA shell). Nothing ran. Retrying is safe.
 *
 *   - **`response_lost`** — the transport failed in a way that leaves it
 *     AMBIGUOUS whether the server-side work ran: no response at all (the
 *     connection dropped, the API restarted mid-call) or a gateway/origin 5xx —
 *     Cloudflare's 524 at its ~100s ceiling being the one that motivated this.
 *     The call is lost; **the work may have completed**. Retrying may DOUBLE it.
 *
 * The distinction is the whole point. On gnexis (2026-08-27) three CRM
 * re-imports finished server-side, were reported as failures, and were
 * re-triggered — each a 34 MB database rewrite — because the app author had no
 * way to tell "the call was lost" from "the script failed".
 *
 * This is honest vocabulary for ANY transport failure, so it stays useful after
 * the async conversion removes the Cloudflare-ceiling *cause*: an API restart
 * mid-run produces the identical ambiguity.
 *
 * ── The SERVER mints `response_lost` too, as of #563 ───────────────────────
 *
 * Everything above reads as a client concern, and until #563 it was one: this
 * vocabulary existed solely as the output of `transportErrorBody`, i.e. what a
 * browser says when it could not read an answer. That is no longer the whole
 * story, and the next reader will otherwise assume it is.
 *
 * `POST /api/v1/scripts/run` now enqueues every run. A client that did not opt
 * into the 202 has its connection HELD until the job lands — and when that wait
 * expires, the API answers `{ ok: false, error: 'response_lost', detail, jobId }`
 * itself, under **HTTP 200**.
 *
 * This is correct usage rather than a borrowed code: the meaning is exactly
 * "the call is lost, the work may have completed". The API stopped watching; the
 * script did not stop. Reporting a script failure there is the defect #471
 * exists to remove, and it replaces a strictly worse answer — Cloudflare's HTML
 * error page, which is not a shape anyone could branch on.
 *
 * Two consequences to keep true:
 *
 *   - **200, not a 5xx.** `isResponseLost(status)` is true for `>= 500`, so on a
 *     5xx any intermediate that mangles the body makes the client mint its OWN
 *     `response_lost` — losing the `jobId` the server attached, on exactly the
 *     path where the transport is already misbehaving. At 200 a parseable body
 *     passes through and the handle survives.
 *   - **`RESPONSE_LOST_DETAIL` is imported, never retyped.** One spelling, as
 *     the constant below says — now across two processes rather than two
 *     browser transports.
 *
 * Zero dependencies on purpose — the SPA's iframe-mode relay
 * (`features/collab/ui/HtmlViewer.tsx`) imports it through the
 * `@papan/sdk/transport-error` subpath, so the two transports cannot drift into
 * describing the same failure differently. Do not add imports here; the subpath
 * exists to keep this out of the SPA's bundle graph for the whole SDK runtime.
 */

/** Stable code for a transport failure that may have left work running. */
export const RESPONSE_LOST = 'response_lost'

/** Stable code for a non-JSON answer that provably did NOT run the work. */
export const INVALID_RESPONSE = 'invalid_response'

/** Human copy for `response_lost`. One spelling, so every surface says it the same way. */
export const RESPONSE_LOST_DETAIL =
  'the connection to Papan was lost before the result came back — the run may still be executing; check its results before retrying'

/**
 * Does this HTTP status leave it ambiguous whether the server-side work ran?
 *
 * `0` is the caller's convention for "no response object at all" (a thrown
 * `fetch`: DNS/TLS/connection-reset, or the tab's network dropping mid-flight).
 * The request may already have been delivered, so it is ambiguous exactly like
 * a 5xx.
 *
 * `>= 500` covers the gateway family — 502/503/504 and Cloudflare's 520-527,
 * of which **524 is the ~100s ceiling** #471 is about. Every one of these is
 * emitted by something IN FRONT OF the origin after the request was forwarded.
 *
 * A **4xx** is deliberately NOT ambiguous: it is a refusal made before the
 * runner was reached (auth, rate limit, bad body), so the work did not start.
 * A **2xx with a junk body** is not ambiguous either — it means the call landed
 * somewhere that is not the API at all (the misrouted-origin failure that
 * serves the SPA shell with `content-type: text/html`), so again nothing ran.
 * Both keep `invalid_response`; widening this predicate to either would tell an
 * author to go hunting for side effects that cannot exist.
 */
export function isResponseLost(status: number): boolean {
  return status === 0 || status >= 500
}

/**
 * The error envelope for a call whose body could not be parsed.
 *
 * `detail` is set only on the ambiguous branch — an author branching on
 * `error === 'response_lost'` gets copy they can show a user, and the
 * unambiguous branch keeps its existing bare shape.
 */
export function transportErrorBody(status: number): { ok: false; error: string; detail?: string } {
  return isResponseLost(status)
    ? { ok: false, error: RESPONSE_LOST, detail: RESPONSE_LOST_DETAIL }
    : { ok: false, error: INVALID_RESPONSE }
}

/**
 * #1517 — `db.run(op, args, { timeoutMs })`: the app's own limit on how long it waits for one call.
 *
 * A request can sit between the browser and the origin for tens of seconds (golink, 2026-10-29: a
 * heartbeat UPDATE took 34 s and an INSERT ended in `Failed to fetch` after 40 s while the API answered
 * every `db.run` it received in under 250 ms). The server can't bound a request it hasn't received,
 * so the limit lives in the transport. It is opt-in, never a default: the server lets a run take up
 * to its 90 s request budget precisely so a write is not reported lost while it can still commit, and
 * the SDK can't tell a read op from a write op. An app that asks for a short limit (a heartbeat) takes
 * that trade knowingly.
 *
 * Past the limit the call answers `timeout`: like `response_lost`, the op MAY have run — a re-read
 * can't prove a write didn't land, so repeat one only if applying it twice is safe. A late answer is dropped.
 */
export const DB_TIMEOUT = 'timeout'

/** How much longer an iframe app waits for its host than the limit the host applies to each request, so
 *  the host's answer (the authoritative one) normally arrives first. */
export const DB_TIMEOUT_RELAY_SLACK_MS = 2_000

/** The longest delay a browser timer honours (2^31−1 ms, ~24.8 days); a longer one fires AT ONCE. */
const MAX_TIMER_MS = 2 ** 31 - 1

/** The app's `timeoutMs`, or `undefined` when it gave none (or nothing usable — a limit of 0, a
 *  negative or non-finite number, or a non-number never shortens a call). A limit too long for a
 *  browser timer (with the relay's slack added) means no limit: it would otherwise fire at once. */
export function dbTimeoutMs(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined
  const ms = Math.ceil(value)
  return ms > MAX_TIMER_MS - DB_TIMEOUT_RELAY_SLACK_MS ? undefined : ms
}

/** The answer for a call that passed its `timeoutMs`. */
export function dbTimedOutBody(ms: number): { ok: false; error: string; detail: string } {
  return { ok: false, error: DB_TIMEOUT, detail: `no answer within ${ms} ms; the op may still have run — repeat a write only if applying it twice is safe` }
}

/** #1517 — an app's `args` that can't be sent as JSON (a `BigInt`, a cycle): nothing was sent, so the op never ran.
 *  Never `response_lost` — that would stop an app from fixing and re-sending a call that certainly didn't land. */
export function dbArgsNotSendableBody(): { ok: false; error: string; detail: string } {
  return { ok: false, error: 'invalid_args', detail: 'args could not be sent as JSON (e.g. a BigInt or a circular value); nothing was sent' }
}
